import {MURAL_TYPES} from '@/features/profile/services/completeProfile.schema'
import {InfiniteData, QueryKey, useMutation} from '@tanstack/react-query'
import {getMediaPackageContentById, getPhotoById, getVideoById} from '../services/feed.http'
import {QUERY_KEYS as FEED_QUERY_KEYS, MUTATION_KEYS as STORIES_KEYS} from '@/features/feed/queries/keys'
import {QUERY_KEYS as CHAT_QUERY_KEYS} from '@/features/chat/queries/keys'
import {QUERY_KEYS as PROFILE_QUERY_KEYS} from '@/features/profile/queries/keys'

import {queryClient} from '@/queryClient'
import {HostFeedResponseGeneralV3, HostStoryType} from '../types'
import {Media} from '@/features/host/types'
import {getReactQueryKey} from '@/utilities/helpers'
import {Message} from '@/features/chat/chat.model'
import {getChatAttachmentById} from '@/features/chat/services/chat.http'

export enum UPDATE_VARIANT {
    MURAL = 'MURAL',
    PROFILE_MURAL = 'PROFILE_MURAL',
    CHAT = 'CHAT',
    STORY = 'STORY'
}

const ACTIONS_MAP = {
    [MURAL_TYPES.PHOTOS]: ({mediaId}: {mediaId: string}) => getPhotoById(mediaId),
    [MURAL_TYPES.VIDEOS]: ({mediaId}: {mediaId: string}) => getVideoById(mediaId),
    [MURAL_TYPES.MEDIA_PACKAGES]: ({mediaId, creatorId}: {mediaId: string; creatorId: string}) =>
        getMediaPackageContentById(mediaId, creatorId),
    [MURAL_TYPES.CHAT]: ({mediaId, chatIdentifier}: {mediaId: string; chatIdentifier: string}) =>
        getChatAttachmentById(chatIdentifier, mediaId)
}

const updateMuralData = (data: Media, queryKey: QueryKey) => {
    queryClient.setQueriesData<InfiniteData<HostFeedResponseGeneralV3>>({queryKey}, oldData => {
        if (!oldData) return oldData
        return {
            ...oldData,
            pages: oldData.pages.map(page => ({
                ...page,
                data: page.data.map(item => (item.id === data.id ? {...item, ...data, blurHash: undefined} : item))
            }))
        }
    })
}

const UPDATE_ACTIONS_MAP = {
    [UPDATE_VARIANT.MURAL]: (data: Media) => {
        const feedV3Key = getReactQueryKey(FEED_QUERY_KEYS.feedV3)
        updateMuralData(data, feedV3Key)
    },
    [UPDATE_VARIANT.PROFILE_MURAL]: (data: Media) => {
        const profileMuralKey = getReactQueryKey(PROFILE_QUERY_KEYS.mural_of_host)
        updateMuralData(data, profileMuralKey)
    },
    [UPDATE_VARIANT.STORY]: (data: Media) => {
        const storyKey = getReactQueryKey(STORIES_KEYS.host_new_post)
        queryClient.setQueriesData<HostStoryType[]>({queryKey: storyKey}, oldData => {
            if (!oldData) return oldData
            return oldData.map(item => {
                const isPhoto = item.photoId
                if (item.photoId === data.id || item.videoId === data.id) {
                    return {
                        ...item,
                        [isPhoto ? 'photo' : 'video']: data,
                        blurHash: undefined
                    }
                }
                return item
            })
        })
    },
    [UPDATE_VARIANT.CHAT]: (data: Media) => {
        const _data = {...data} as Media & {receiverId: string; senderId: string}
        const fetchMessagesKey = getReactQueryKey(CHAT_QUERY_KEYS.fetchMessages).map((item, idx) =>
            idx === 1 ? `${_data.receiverId}-${_data.senderId}` : item
        ) as QueryKey
        queryClient.setQueriesData<InfiniteData<Message[]>>({queryKey: fetchMessagesKey}, oldData => {
            if (!oldData) return oldData
            return {
                ...oldData,
                pages: oldData.pages.map(page =>
                    page.map(message => {
                        if (!message.metadata.chatAttachment) return message
                        const chatAttachment = message.metadata.chatAttachment
                        return chatAttachment.id === data.id
                            ? {
                                  ...message,
                                  metadata: {
                                      ...message.metadata,
                                      chatAttachment: {...chatAttachment, ...data, blurHash: undefined}
                                  }
                              }
                            : message
                    })
                )
            }
        })
    }
}

export const useGetContentById = ({
    mediaType,
    variant,
    creatorId,
    chatIdentifier
}: {
    mediaType: MURAL_TYPES
    variant: UPDATE_VARIANT
    creatorId: string
    chatIdentifier: string
}) => {
    return useMutation({
        mutationFn: (mediaId: string) => ACTIONS_MAP[mediaType]({mediaId, creatorId, chatIdentifier}),
        onSuccess: data => {
            UPDATE_ACTIONS_MAP[variant]?.(data)
        }
    })
}
